import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Completați câmpurile obligatorii.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone || '-',
          service: form.service || '-',
          message: form.message,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '', service: '' });
    } catch {
      setError('A apărut o eroare. Încercați din nou.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-32 bg-white dark:bg-[#242322]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
            Contactează-ne
          </p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
            Solicită o consultanță gratuită
          </h2>
          <p className="text-base leading-relaxed text-[#6B6561] dark:text-[#9B9590] mt-4 max-w-lg mx-auto">
            Spune-ne ce ai în minte și te vom contacta în cel mai scurt timp cu o ofertă personalizată.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success-message"
                className="text-center py-16 bg-[#F9F6F0] dark:bg-[#1A1918] rounded-lg"
              >
                <CheckCircle size={48} className="text-[#7C8B74] mx-auto mb-4" />
                <h3 className="font-['Outfit'] text-2xl font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-2">
                  Mesaj trimis cu succes!
                </h3>
                <p className="text-[#6B6561] dark:text-[#9B9590]">Vă vom contacta în cel mai scurt timp.</p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="mt-6 px-6 py-2.5 bg-[#A67B5B] text-white text-sm font-medium rounded-full hover:bg-[#8B654A] transition-colors"
                >
                  Trimite alt mesaj
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="text-sm font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-1.5 block">
                      Nume complet *
                    </label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Ion Popescu"
                      autoComplete="name"
                      className="border-[#E3DACB] dark:border-[#3D3A37] focus-visible:ring-[#A67B5B] bg-[#F9F6F0]/50 dark:bg-[#1A1918]/50 dark:text-[#F0EDE8] h-11"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-sm font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-1.5 block">
                      Email *
                    </label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="ion@exemplu.ro"
                      autoComplete="email"
                      className="border-[#E3DACB] dark:border-[#3D3A37] focus-visible:ring-[#A67B5B] bg-[#F9F6F0]/50 dark:bg-[#1A1918]/50 dark:text-[#F0EDE8] h-11"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-phone" className="text-sm font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-1.5 block">
                      Telefon
                    </label>
                    <Input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+40 7XX XXX XXX"
                      autoComplete="tel"
                      className="border-[#E3DACB] dark:border-[#3D3A37] focus-visible:ring-[#A67B5B] bg-[#F9F6F0]/50 dark:bg-[#1A1918]/50 dark:text-[#F0EDE8] h-11"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="text-sm font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-1.5 block">
                      Serviciu dorit
                    </label>
                    <select
                      id="contact-service"
                      data-testid="contact-service-select"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="flex h-11 w-full rounded-md border border-[#E3DACB] dark:border-[#3D3A37] bg-[#F9F6F0]/50 dark:bg-[#1A1918]/50 px-3 py-2 text-sm text-[#2C2A29] dark:text-[#F0EDE8] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#A67B5B]"
                    >
                      <option value="">Selectează serviciul</option>
                      <option value="bucatarie">Mobilier bucătărie</option>
                      <option value="living">Mobilier living</option>
                      <option value="dormitor">Mobilier dormitor</option>
                      <option value="birou">Mobilier birou</option>
                      <option value="comercial">Mobilier comercial</option>
                      <option value="consultanta">Consultanță & Design</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="text-sm font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-1.5 block">
                    Mesajul tău *
                  </label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Descrie proiectul tău sau întreabă-ne orice..."
                    rows={5}
                    className="border-[#E3DACB] dark:border-[#3D3A37] focus-visible:ring-[#A67B5B] bg-[#F9F6F0]/50 dark:bg-[#1A1918]/50 dark:text-[#F0EDE8]"
                  />
                </div>

                {error && (
                  <p role="alert" data-testid="contact-error" className="text-sm text-red-600">{error}</p>
                )}

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  aria-busy={sending}
                  className="group px-8 py-3.5 bg-[#A67B5B] text-white text-sm font-medium rounded-full hover:bg-[#8B654A] transition-all duration-300 flex items-center gap-2 disabled:opacity-60"
                >
                  {sending ? 'Se trimite...' : 'Trimite mesajul'}
                  <Send size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-6">
                Informații de contact
              </h3>
              <div className="space-y-5">
                <a
                  href="tel:+40738562505"
                  data-testid="contact-phone-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#A67B5B]/10 dark:bg-[#A67B5B]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#A67B5B]/20 dark:group-hover:bg-[#A67B5B]/30 transition-colors">
                    <Phone size={18} className="text-[#A67B5B]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6561] dark:text-[#9B9590]">Telefon</p>
                    <p className="text-base font-medium text-[#2C2A29] dark:text-[#F0EDE8]">+40 738 562 505</p>
                  </div>
                </a>

                <a
                  href="mailto:contact@mobilatamob.ro"
                  data-testid="contact-email-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#A67B5B]/10 dark:bg-[#A67B5B]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#A67B5B]/20 dark:group-hover:bg-[#A67B5B]/30 transition-colors">
                    <Mail size={18} className="text-[#A67B5B]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6561] dark:text-[#9B9590]">Email</p>
                    <p className="text-base font-medium text-[#2C2A29] dark:text-[#F0EDE8]">contact@mobilatamob.ro</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#A67B5B]/10 dark:bg-[#A67B5B]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#A67B5B]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6561] dark:text-[#9B9590]">Adresă</p>
                    <p className="text-base font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
                      Strada Nicolae Labiș nr. 114,<br />Dumbrăveni, jud. Suceava
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F6F0] dark:bg-[#1A1918] rounded-lg p-6">
              <h4 className="font-['Outfit'] font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-2">
                Program de lucru
              </h4>
              <div className="space-y-1.5 text-sm text-[#6B6561] dark:text-[#9B9590]">
                <p>Luni - Vineri: 08:00 - 18:00</p>
                <p>Sâmbătă: Închis</p>
                <p>Duminică: Închis</p>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden border border-[#E3DACB] dark:border-[#3D3A37]" data-testid="google-maps-embed">
              <iframe
                title="Locația Mobila ta Mob"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2687.4957169620584!2d26.41997697614698!3d47.655365584609115!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4734e5002c5e05b5%3A0x7690b00a308d2cfc!2sMobila%20Ta%20Mob!5e0!3m2!1sro!2sro!4v1776694388946!5m2!1sro!2sro"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
